import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, SidePanelFormControl, SseFeedbackControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import CardGridControl from "../components/CardGridControl";
import WarehouseStepSelectorControl from "../components/WarehouseStepSelectorControl";
import ResourceStateControl from "../components/ResourceStateControl";
import { getSourceObjectKey as getSourceObjectIdentityKey } from "../components/wizard/resourceConfigWizardUtils";
import { sanitizeHtmlFragment } from "../../../utilities/markupSanitizer";
import {
  buildDestinationManualRunField,
  DESTINATION_MANUAL_RUN_AUDIENCE,
  getArray,
  getBoolean,
  getDeploymentDisplayName,
  getDeploymentId,
  getDestinationManualRunEntityOptions,
  getDestinationManualRunModeDefinitions,
  DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE,
  DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS,
  DESTINATION_MANUAL_RUN_PROCESSOR_TYPES,
  getDestinationManualRunSelectedMode,
  getDeploymentRenderKey,
  getDisplayName,
  getEntityDeploymentId,
  getId,
  getString,
  getValue,
  isRecordActive,
  isRecordDeleted,
  isSchemaDestinationConfigEditable,
  normalizeDestinationManualRunEntityIds,
  normalizeDestinationManualRunValues,
  validateWarehouseDeployValues,
  normalizeCompare
} from "./resourceWorkspace.destinationUtils";

const DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE = DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.realTime;
const MANUAL_RUN_ENTITY_SELECTION_ERROR = "Select at least one Entity Object before continuing.";

const buildSelectListField = (fieldName, displayName, options) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataJson: options,
  IsRequired: false
});

const isEndpointDefaults = (defaults) =>
  Boolean(defaults?.isEndpoint) || normalizeCompare(defaults?.managerKey) === "schemaapi";

const isEndpointDeploymentViewOnly = (defaults, deployment) =>
  isEndpointDefaults(defaults) && !isSchemaDestinationConfigEditable(deployment);

const SCHEMA_SCOPE_IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]+$/;

export const createSchemaScopeClientId = () => {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (randomUuid) return randomUuid;
  return `schema-scope-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
};

const cleanGeneratedSchemaScopeIdentifier = (value) =>
  String(value || "").replace(/[^A-Za-z0-9]/g, "");

export const generateSchemaScopeIdentifier = (schemaName, usedIdentifiers = []) => {
  const used = new Set(getArray({ usedIdentifiers }, "usedIdentifiers").map((identifier) => normalizeCompare(identifier)).filter(Boolean));
  const words = String(schemaName || "")
    .split(/[^A-Za-z0-9]+/)
    .map((word) => word.trim())
    .filter(Boolean);
  const initials = words.map((word) => word[0]).join("");
  const expanded = words.map((word) => word.slice(0, Math.min(2, word.length))).join("");
  const candidates = [initials, expanded, schemaName]
    .map(cleanGeneratedSchemaScopeIdentifier)
    .filter(Boolean);
  let candidate = candidates.find((entry) => !used.has(normalizeCompare(entry))) || "";
  if (!candidate) return "";
  const base = candidate;
  let index = 2;
  while (used.has(normalizeCompare(candidate))) {
    candidate = `${base}${index}`;
    index += 1;
  }
  return candidate;
};

export const getSchemaScopeRowKey = (scope, index = 0) =>
  getString(scope, "UniqueId", "uniqueId", "_id", "id", "__scopeKey") || `schema-scope-${index}`;

export const stripSchemaScopeUiState = (scope) => {
  const { __scopeKey, __validationMessage, ...cleanScope } = scope || {};
  return cleanScope;
};

export const normalizeSchemaScopeRows = (schemaScopes, deployment) => {
  const used = [];
  const sourceRows = getArray({ SchemaScopes: schemaScopes }, "SchemaScopes");
  const rows = sourceRows.length ? sourceRows : [{
    SchemaId: getString(deployment, "SchemaId", "schemaId"),
    SchemaName: getString(deployment, "SchemaName", "schemaName"),
    SchemaVersionId: getString(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId"),
    SchemaVersion: getString(deployment, "CurrentSchemaVersion", "currentSchemaVersion"),
    IsActive: true,
    IsDeleted: false,
    Sequence: 1
  }];

  return rows.map((scope, index) => {
    const schemaName = getString(scope, "SchemaName", "schemaName") || getString(deployment, "SchemaName", "schemaName");
    const schemaIdentifierSource = schemaName || getString(scope, "SchemaId", "schemaId") || getString(deployment, "SchemaId", "schemaId");
    const existingIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    const identifier = existingIdentifier || generateSchemaScopeIdentifier(schemaIdentifierSource, used);
    if (identifier) used.push(identifier);
    return {
      ...scope,
      __scopeKey: getSchemaScopeRowKey(scope, index) || createSchemaScopeClientId(),
      SchemaName: schemaName,
      SchemaScopeIdentifier: identifier,
      Sequence: getValue(scope, "Sequence", "sequence") || index + 1,
      IsActive: getValue(scope, "IsActive", "isActive") !== false,
      IsDeleted: getValue(scope, "IsDeleted", "isDeleted") === true
    };
  });
};

export const validateSchemaScopeRows = (rows) => {
  const used = new Map();
  return rows.map((scope) => {
    const identifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    let validationMessage = "";
    if (!identifier) {
      validationMessage = "Identifier is required.";
    } else if (!SCHEMA_SCOPE_IDENTIFIER_PATTERN.test(identifier)) {
      validationMessage = "Use only letters, numbers, underscores, and dashes.";
    } else {
      const normalized = normalizeCompare(identifier);
      if (used.has(normalized)) {
        validationMessage = `Duplicate identifier '${identifier}'.`;
      }
      used.set(normalized, true);
    }
    return { ...scope, __validationMessage: validationMessage };
  });
};

export const SCHEMA_SCOPE_FORM_METADATA = {
  Name: "SchemaAPISchemaScope",
  ItemName: "SchemaAPISchemaScope",
  DisplayName: "Schema Scope",
  SidePanelFormWidth: 42,
  Fields: [
    { FieldName: "SchemaScopeIdentifier", DisplayName: "Scope Identifier", Description: "Route-safe schema scope identifier used in API and MCP requests.", FieldType: SystemTypes.UIFieldType.TextBox, IsRequired: true, TabId: "Scope|1", DisplaySequence: 1 },
    { FieldName: "SchemaName", DisplayName: "Schema", FieldType: SystemTypes.UIFieldType.Label, IsRequired: false, TabId: "Scope|1", DisplaySequence: 2 },
    { FieldName: "SchemaVersion", DisplayName: "Schema Version", FieldType: SystemTypes.UIFieldType.Label, IsRequired: false, TabId: "Scope|1", DisplaySequence: 3 },
    { FieldName: "Sequence", DisplayName: "Sequence", FieldType: SystemTypes.UIFieldType.TextBox, Format: SystemTypes.UIFormats?.INT, IsRequired: false, TabId: "Scope|1", DisplaySequence: 4 },
    { FieldName: "IsActive", DisplayName: "Active", FieldType: SystemTypes.UIFieldType.CheckBox, Format: SystemTypes.UIFormats?.BOOL, IsRequired: false, TabId: "Status|2", DisplaySequence: 1 }
  ]
};

const SCHEMA_SCOPE_CARD_FIELDS = [
  { FieldName: "SchemaName", DisplayName: "Schema", ShowOnCard: true },
  { FieldName: "SchemaVersion", DisplayName: "Version", ShowOnCard: true },
  { FieldName: "__validationMessage", DisplayName: "Validation", ShowOnCard: true }
];

export const buildSchemaScopeDraft = (deployment, existingScopes = []) => {
  const existingIdentifiers = getArray({ existingScopes }, "existingScopes").map((scope) => getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier"));
  const schemaName = getString(deployment, "SchemaName", "schemaName");
  const schemaId = getString(deployment, "SchemaId", "schemaId");
  return {
    __scopeKey: createSchemaScopeClientId(),
    UniqueId: createSchemaScopeClientId(),
    SchemaId: schemaId,
    SchemaName: schemaName,
    SchemaVersionId: getString(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId"),
    SchemaVersion: getString(deployment, "CurrentSchemaVersion", "currentSchemaVersion"),
    SchemaScopeIdentifier: generateSchemaScopeIdentifier(schemaName || schemaId, existingIdentifiers),
    Sequence: getArray({ existingScopes }, "existingScopes").length + 1,
    IsActive: true,
    IsDeleted: false
  };
};

const SchemaScopesTab = ({
  deployment,
  value,
  disabled,
  onChange
}) => {
  const [editingScope, setEditingScope] = useState(null);
  const scopeRows = useMemo(
    () => validateSchemaScopeRows(normalizeSchemaScopeRows(value, deployment)),
    [deployment, value]
  );

  const emitRows = useCallback((nextRows) => {
    onChange?.(nextRows.map(stripSchemaScopeUiState));
  }, [onChange]);

  const updateScope = useCallback((updatedScope) => {
    if (!updatedScope) return;
    const updatedKey = getSchemaScopeRowKey(updatedScope);
    const nextRows = scopeRows.map((scope) =>
      String(getSchemaScopeRowKey(scope)) === String(updatedKey)
        ? { ...scope, ...updatedScope }
        : scope);
    emitRows(nextRows);
    setEditingScope(null);
  }, [emitRows, scopeRows]);

  const setScopeActive = useCallback((scopeToUpdate, isActive) => {
    const updatedKey = getSchemaScopeRowKey(scopeToUpdate);
    emitRows(scopeRows.map((scope) =>
      String(getSchemaScopeRowKey(scope)) === String(updatedKey)
        ? { ...scope, IsActive: isActive }
        : scope));
  }, [emitRows, scopeRows]);

  const addScope = useCallback(() => {
    const nextScope = buildSchemaScopeDraft(deployment, scopeRows);
    emitRows([...scopeRows, nextScope]);
    setEditingScope(nextScope);
  }, [deployment, emitRows, scopeRows]);

  return (
    <div className="resource-workspace-schema-scopes-tab">
      <CardGridControl
        className="resource-workspace-schema-scopes-grid"
        rows={scopeRows}
        cardFields={SCHEMA_SCOPE_CARD_FIELDS}
        rowKey={getSchemaScopeRowKey}
        cardTitleResolver={(scope) => getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier") || "Schema Scope"}
        readOnly={disabled}
        disableInlineEdit
        disableCardClickEdit
        searchable
        activeFilterEnabled
        emptyContent="No schema scopes are configured."
        toolbarLeadingContent={<span>{scopeRows.length.toLocaleString()} scopes</span>}
        toolbarActions={[{
          key: "add-schema-scope",
          title: "Add schema scope",
          label: "Add Scope",
          iconKey: "add",
          disabled,
          onClick: addScope
        }]}
        headerIconActions={[
          {
            key: "disable-schema-scope",
            title: "Disable scope",
            iconKey: "undo",
            visible: (scope) => getValue(scope, "IsActive", "isActive") !== false,
            disabled,
            onClick: (scope) => setScopeActive(scope, false)
          },
          {
            key: "enable-schema-scope",
            title: "Enable scope",
            iconKey: "success",
            visible: (scope) => getValue(scope, "IsActive", "isActive") === false,
            disabled,
            onClick: (scope) => setScopeActive(scope, true)
          },
          {
            key: "edit-schema-scope",
            title: "Edit scope",
            iconKey: "edit",
            disabled,
            onClick: (scope) => setEditingScope(scope)
          }
        ]}
      />
      <SidePanelFormControl
        metadata={SCHEMA_SCOPE_FORM_METADATA}
        title={editingScope ? `Edit ${getString(editingScope, "SchemaScopeIdentifier", "schemaScopeIdentifier") || "Schema Scope"}` : "Edit Schema Scope"}
        formFields={SCHEMA_SCOPE_FORM_METADATA.Fields}
        open={Boolean(editingScope)}
        itemData={editingScope}
        setItemData={setEditingScope}
        setOpenEditSidePanel={(isOpen) => {
          if (!isOpen) setEditingScope(null);
        }}
        onUpdateSidePanel={() => {}}
        onCancel={() => setEditingScope(null)}
        onSave={updateScope}
        saveButtonText="Save"
        viewOnly={disabled}
      />
    </div>
  );
};

const MANUAL_RUN_ENTITY_SELECTOR_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Entity Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorSourceObjectOfRecord", DisplayName: "Source Object of Record", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const ENTITY_DEPLOYMENT_PICKER_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Entity Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorSourceObjectOfRecord", DisplayName: "Source Object of Record", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const getManualRunEntityObjectId = (entityObject) =>
  getString(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId", "_id", "id");

const getManualRunSourceObjectDisplayName = (sourceObject) =>
  getString(sourceObject, "DisplayName", "displayName", "Text", "text", "Name", "name", "SystemName", "systemName", "ObjectName", "objectName");

const getManualRunSourceObjectIds = (sourceObject) => [
  getString(sourceObject, "_id", "id"),
  getString(sourceObject, "Value", "value"),
  getString(sourceObject, "UniqueId", "uniqueId"),
  getString(sourceObject, "SourceObjectId", "sourceObjectId", "SourceObjectID", "EntityObjectID", "EntityObjectId", "entityObjectId"),
  getSourceObjectIdentityKey(sourceObject)
].filter(Boolean);

const getManualRunEntitySourceObjectId = (entityObject) =>
  getString(
    entityObject,
    "SourceObjectOfRecordId",
    "sourceObjectOfRecordId",
    "SourceObjectOfRecordID",
    "sourceObjectOfRecordID",
    "SourceObjectId",
    "sourceObjectId",
    "SourceObjectID"
  );

const getManualRunEntitySourceObjectDisplayName = (entityObject, sourceObjectsById = new Map()) => {
  const directName = getString(
    entityObject,
    "SourceObjectOfRecordDisplayName",
    "sourceObjectOfRecordDisplayName",
    "SourceObjectOfRecordName",
    "sourceObjectOfRecordName",
    "SourceObjectDisplayName",
    "sourceObjectDisplayName",
    "SourceObjectName",
    "sourceObjectName"
  );
  if (directName) return directName;
  const sourceObjectId = getManualRunEntitySourceObjectId(entityObject);
  return sourceObjectId ? sourceObjectsById.get(sourceObjectId.toLowerCase()) || "" : "";
};

const getManualRunEntitySelectorHeaderStatus = (row) => {
  const active = row?.IsActive !== false && row?.isActive !== false;
  return {
    title: active ? "Active" : "Inactive",
    tone: active ? "active" : "inactive"
  };
};

const getEntityDeploymentPickerHeaderStatus = (row) => {
  const active = isRecordActive(row);
  return {
    title: active ? "Active" : "Inactive",
    tone: active ? "active" : "inactive"
  };
};

const MCP_FIELD_NAMES = new Set([
  "mcpexecutionidentitymode",
  "mcpauthorizationmode",
  "mcpauthorizedprofiles",
  "exposerelationshipmetadata",
  "mcpoauthscopes",
  "mcpconnectorurl",
  "mcpsetupinstructions"
]);

const isMcpSurfaceEnabled = (deployment) => {
  const rawSurfaceMode = getString(deployment, "PublicSurfaceMode", "publicSurfaceMode");
  const surfaceMode = Number(rawSurfaceMode);
  if (Number.isFinite(surfaceMode) && surfaceMode > 0) {
    return surfaceMode === 2 || surfaceMode === 3;
  }

  const normalizedSurfaceMode = normalizeCompare(rawSurfaceMode);
  if (normalizedSurfaceMode === "mcponly" || normalizedSurfaceMode === "both" || normalizedSurfaceMode === "apiandmcp") {
    return true;
  }

  return getBoolean(deployment, "McpEnabled", "mcpEnabled") === true;
};

const isMcpDeploymentField = (field) =>
  normalizeCompare(field?.TabId).startsWith("mcp|") ||
  normalizeCompare(field?.GroupId).startsWith("mcp|") ||
  MCP_FIELD_NAMES.has(normalizeCompare(field?.FieldName));

const ENDPOINT_DEPLOYMENT_CONFIG_FIELD_NAMES = new Set([
  "deploymentname",
  "maxconcurrency"
]);

const moveEndpointDeploymentConfigField = (field) => {
  if (!ENDPOINT_DEPLOYMENT_CONFIG_FIELD_NAMES.has(normalizeCompare(field?.FieldName))) return field;

  const sequence = normalizeCompare(field?.FieldName) === "deploymentname" ? 4 : 5;
  return {
    ...field,
    TabId: "Endpoint|1",
    GroupId: "Endpoint Config|2",
    DisplaySequence: sequence
  };
};

const getDeploymentEditFields = (metadata, defaults, deployment) => {
  const fields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];
  if (!isEndpointDefaults(defaults)) return fields;
  const visibleFields = isMcpSurfaceEnabled(deployment)
    ? fields
    : fields.filter((field) => !isMcpDeploymentField(field));

  return visibleFields.map((field) => {
    const endpointField = moveEndpointDeploymentConfigField(field);
    if (normalizeCompare(endpointField?.FieldName) !== "publicsurfacemode") return endpointField;
    return {
      ...endpointField,
      Disabled: true,
      IsReadOnly: true
    };
  });
};

export const ResourceDestinationEditPanel = ({
  open,
  destination,
  metadata,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeDestination
}) => {
  const destinationName = getDisplayName(destination, getDisplayName(metadata, "Endpoint"));
  const formFields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];
  const isNew = Boolean(destination?.IsNew ?? destination?.isNew);

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getId(destination) || "destination"}-${open ? "open" : "closed"}`}
      open={open}
      title={isNew ? `Add ${destinationName}` : `Edit: ${destinationName}`}
      metadata={metadata}
      formFields={formFields}
      itemData={destination}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("destination-edit:") || state?.busyAction === "destination-add" ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeDestination(null);
        }
      }}
      setItemData={onChangeDestination}
      viewOnly={!metadata}
    />
  );
};

export const ResourceDestinationDeploymentEditPanel = ({
  open,
  deployment,
  metadata: systemMetadata,
  defaults,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeDeployment
}) => {
  const deploymentName = getDisplayName(deployment, "Deployment");
  const metadata = systemMetadata;
  const formFields = getDeploymentEditFields(metadata, defaults, deployment);
  const viewOnly = !metadata || isEndpointDeploymentViewOnly(defaults, deployment);
  const schemaScopeSubGridRenderers = useMemo(() => {
    if (!isEndpointDefaults(defaults)) return undefined;
    return {
      SchemaScopes: ({ value, onChange, disabled }) => (
        <SchemaScopesTab
          deployment={deployment}
          value={value}
          disabled={disabled || viewOnly}
          onChange={onChange}
        />
      )
    };
  }, [defaults, deployment, viewOnly]);

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getDeploymentRenderKey(deployment, defaults)}-${open ? "open" : "closed"}`}
      open={open}
      title={`${viewOnly ? "View" : "Edit"}: ${deploymentName}`}
      metadata={metadata}
      formFields={formFields}
      itemData={deployment}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("destination-deployment-save:") ? "Saving..." : "Save"}
      subGridRenderers={schemaScopeSubGridRenderers}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeDeployment(null);
        }
      }}
      setItemData={onChangeDeployment}
      viewOnly={viewOnly}
    />
  );
};

export const ResourceDestinationEntityDeploymentEditPanel = ({
  open,
  entityDeployment,
  metadata,
  defaults,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeEntityDeployment
}) => {
  const entityDeploymentName = getString(entityDeployment, "EntityObjectName", "entityObjectName") || "Entity Deployment Scope";
  const hideFilePackagingMode = normalizeCompare(defaults?.managerKey) === "warehouse";
  const formFields = Array.isArray(metadata?.Fields)
    ? metadata.Fields.filter((field) => !hideFilePackagingMode || normalizeCompare(field?.FieldName) !== "filepackagingmode")
    : [];
  const viewOnly = !metadata || Boolean(entityDeployment?.__EndpointScopeViewOnly);
  const itemData = {
    ...(entityDeployment || {}),
    EntityDisplayName: getString(entityDeployment, "EntityDisplayName", "entityDisplayName") || getString(entityDeployment, "EntityObjectName", "entityObjectName")
  };

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getEntityDeploymentId(entityDeployment) || "destination-entity-deployment"}-${open ? "open" : "closed"}`}
      title={`${viewOnly ? "View" : "Edit"}: ${entityDeploymentName}`}
      open={open}
      metadata={metadata}
      formFields={formFields}
      itemData={itemData}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("destination-entity-deployment-save:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeEntityDeployment(null);
        }
      }}
      setItemData={onChangeEntityDeployment}
      viewOnly={viewOnly}
    />
  );
};

export const DestinationEntityDeploymentDeleteDialog = ({
  deleteRequest,
  busy,
  error,
  onCancel,
  onConfirm
}) => {
  const entityDeployment = deleteRequest?.entityDeployment;
  const entityName = getString(entityDeployment, "EntityObjectName", "entityObjectName", "EntityDisplayName", "entityDisplayName") || "Entity deployment scope";
  return (
    <DialogControl
      controlOrigin="destination-entity-deployment-delete"
      title="Delete Entity Deployment Scope"
      openDialog={Boolean(entityDeployment)}
      okText={busy ? "Deleting..." : "Delete"}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <p>Delete "{entityName}" from the deployment scope? This cannot be undone.</p>
        {error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div> : null}
      </div>
    </DialogControl>
  );
};

export const DestinationDeploymentDeleteDialog = ({
  deleteRequest,
  busy,
  error,
  onCancel,
  onConfirm
}) => {
  const deployment = deleteRequest?.deployment;
  const deploymentName = getDeploymentDisplayName(
    deployment,
    deleteRequest?.destination,
    deleteRequest?.schemaVersionLabel || ""
  ) || "Deployment";
  const isEndpointDeployment = Boolean(deleteRequest?.defaults?.isEndpoint);
  return (
    <DialogControl
      controlOrigin="destination-deployment-delete"
      title="Delete Deployment"
      openDialog={Boolean(deployment)}
      okText={busy ? "Deleting..." : "Delete"}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <p>
          Delete "{deploymentName}" from this {isEndpointDeployment ? "Endpoint" : "Destination"}?
          {isEndpointDeployment ? " Its associated Endpoint Scope rows will also be deleted." : ""} This cannot be undone.
        </p>
        {error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div> : null}
      </div>
    </DialogControl>
  );
};

export const DestinationDeploymentActiveConfirmDialog = ({
  request,
  onCancel,
  onConfirm
}) => {
  const deployment = request?.deployment;
  const isEnable = request?.isActive === true;
  const deploymentName = getDeploymentDisplayName(deployment, null, "") || "selected deployment";
  const title = isEnable ? "Confirm Deployment Enable" : "Confirm Deployment Disable";
  const okText = isEnable ? "Enable" : "Disable";

  return (
    <DialogControl
      controlOrigin="destination-deployment-active-confirm"
      title={title}
      openDialog={Boolean(request?.open)}
      okText={okText}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <p>
          {isEnable
            ? `Enable "${deploymentName}"? This will mark the deployment active and turn on Enable Sync on both the destination and this deployment.`
            : `Disable "${deploymentName}"? This will only mark the deployment inactive. Destination and deployment sync settings will not be changed.`}
        </p>
      </div>
    </DialogControl>
  );
};

export const DestinationDeploymentManualRunDialog = ({
  open,
  destination,
  deployment,
  defaults,
  entityObjects,
  sourceObjects = [],
  canEditSingleThreadMode = false,
  values,
  busy,
  error,
  feedback,
  feedbackRef,
  feedbackManager,
  feedbackOperationName,
  feedbackRefId,
  onChange,
  onCancel,
  onConfirm
}) => {
  const managerKey = normalizeCompare(defaults?.managerKey);
  const deploymentName = getDeploymentDisplayName(deployment, destination);
  const [activeStep, setActiveStep] = useState("entities");
  const [localError, setLocalError] = useState("");
  const [activeFeedbackTab, setActiveFeedbackTab] = useState("messages");
  const entityOptions = useMemo(
    () => getDestinationManualRunEntityOptions(entityObjects, deployment, destination),
    [destination, deployment, entityObjects]
  );
  const sourceObjectsById = useMemo(() => {
    const lookup = new Map();
    getArray({ sourceObjects }, "sourceObjects").forEach((sourceObject) => {
      const displayName = getManualRunSourceObjectDisplayName(sourceObject);
      if (!displayName) return;
      getManualRunSourceObjectIds(sourceObject).forEach((id) => lookup.set(id.toLowerCase(), displayName));
    });
    return lookup;
  }, [sourceObjects]);
  const entityObjectsById = useMemo(() => {
    const lookup = new Map();
    getArray({ entityObjects }, "entityObjects").forEach((entityObject) => {
      const id = getManualRunEntityObjectId(entityObject);
      if (id) lookup.set(id.toLowerCase(), entityObject);
    });
    return lookup;
  }, [entityObjects]);
  const entityRows = useMemo(() => entityOptions.map((option) => {
    const entityObject = entityObjectsById.get(String(option.Value || "").toLowerCase()) || {};
    return {
      ...entityObject,
      ManualRunEntityObjectId: option.Value,
      EntitySelectorName: option.Text,
      EntitySelectorObject: getString(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || option.Value,
      EntitySelectorSourceObjectOfRecord: getManualRunEntitySourceObjectDisplayName(entityObject, sourceObjectsById)
    };
  }), [entityObjectsById, entityOptions, sourceObjectsById]);
  const entityIds = useMemo(() => normalizeDestinationManualRunEntityIds(values?.eoids), [values?.eoids]);
  const modeDefinitions = useMemo(
    () => getDestinationManualRunModeDefinitions(defaults, canEditSingleThreadMode),
    [canEditSingleThreadMode, defaults]
  );
  const modeRows = useMemo(() => modeDefinitions.map((mode) => ({
    ModeKey: mode.key,
    DisplayName: mode.label,
    Details: mode.description,
    Impact: mode.audience === DESTINATION_MANUAL_RUN_AUDIENCE.admin ? `${mode.impact} (Admin Only)` : mode.impact,
    SourceMode: mode
  })), [modeDefinitions]);
  const selectedMode = useMemo(
    () => getDestinationManualRunSelectedMode(defaults, values, canEditSingleThreadMode),
    [canEditSingleThreadMode, defaults, values]
  );
  const selectableEntityIds = useMemo(() => entityOptions.map((option) => option.Value).filter(Boolean), [entityOptions]);
  const canEditAdminManualRunOptions = canEditSingleThreadMode;
  const hasVisibleOptions = Boolean(
    (managerKey === "filestorage" && canEditSingleThreadMode)
    || (managerKey === "warehouse" && (
      selectedMode?.supportsResetCursor
      || selectedMode?.supportsForceReconcile
      || (selectedMode?.supportsParallelProcessing && canEditAdminManualRunOptions)
      || selectedMode?.supportsFirstRunReset
    ))
    || (managerKey === "externalsystem" && selectedMode?.supportsRecordLimit)
    || canEditAdminManualRunOptions
  );
  const updateField = useCallback((fieldName, nextValue) => {
    const nextValues = {
      ...(values || {}),
      [fieldName]: nextValue
    };
    if (fieldName === "externalSystemOperation") {
      const currentTake = String(values?.take ?? "").trim();
      if (!currentTake || currentTake === "25" || currentTake === "250") {
        nextValues.take = nextValue === "preview" ? "25" : "250";
      }
    }
    onChange?.(normalizeDestinationManualRunValues(defaults, nextValues, selectedMode));
    setLocalError("");
  }, [defaults, onChange, selectedMode, values]);
  const updateValues = useCallback((nextValues, mode = selectedMode) => {
    onChange?.(normalizeDestinationManualRunValues(defaults, nextValues, mode));
    setLocalError("");
  }, [defaults, onChange, selectedMode]);
  const selectAllEntities = useCallback(() => {
    const allSelected = selectableEntityIds.length > 0 && entityIds.length === selectableEntityIds.length;
    updateValues({ ...(values || {}), eoids: allSelected ? [] : selectableEntityIds });
  }, [entityIds.length, selectableEntityIds, updateValues, values]);
  const allEntitiesSelected = selectableEntityIds.length > 0 && entityIds.length === selectableEntityIds.length;
  const entitySelectionError = activeStep === "entities" && entityIds.length === 0
    ? MANUAL_RUN_ENTITY_SELECTION_ERROR
    : localError;
  const entitySelectorToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar-shell">
      <div className="resource-wizard__entity-scope-selector-toolbar">
        <span>{entityIds.length} selected</span>
        <div className="resource-wizard__entity-scope-selector-actions">
          <ButtonControl
            type="cancel-small"
            onClick={selectAllEntities}
            disabled={busy || entityOptions.length === 0}
          >
            {allEntitiesSelected ? "Unselect All" : "Select All"}
          </ButtonControl>
        </div>
      </div>
      {entitySelectionError ? <span className="resource-wizard__entity-scope-selector-message">{entitySelectionError}</span> : null}
    </div>
  ), [allEntitiesSelected, busy, entityIds.length, entityOptions.length, entitySelectionError, selectAllEntities]);
  const selectMode = useCallback((mode) => {
    updateValues({ ...(values || {}), runMode: mode.key }, mode);
  }, [updateValues, values]);
  const selectModeByKeys = useCallback((nextKeys = []) => {
    const nextMode = modeDefinitions.find((mode) => String(mode.key) === String(nextKeys[0]));
    if (nextMode) selectMode(nextMode);
  }, [modeDefinitions, selectMode]);
  const validateStep = useCallback((stepKey = activeStep) => {
    if (stepKey === "entities" && entityIds.length === 0) {
      setLocalError(MANUAL_RUN_ENTITY_SELECTION_ERROR);
      return false;
    }
    if (stepKey === "options" && selectedMode?.supportsResetCursor && !String(values?.resetfromdate || "").trim() && !String(values?.resetfromvalue || "").trim()) {
      setLocalError("Enter a reset date or reset identifier before running Reset Cursors Only.");
      return false;
    }
    setLocalError("");
    return true;
  }, [activeStep, entityIds.length, selectedMode?.supportsResetCursor, values?.resetfromdate, values?.resetfromvalue]);
  const handleBack = useCallback(() => {
    setLocalError("");
    setActiveStep((current) => current === "options" ? "mode" : "entities");
  }, []);
  const handleOk = useCallback(() => {
    if (activeStep === "entities") {
      if (validateStep("entities")) setActiveStep("mode");
      return;
    }
    if (activeStep === "mode") {
      setLocalError("");
      setActiveStep("options");
      return;
    }
    if (validateStep("options")) onConfirm?.();
  }, [activeStep, onConfirm, validateStep]);

  useEffect(() => {
    if (!open) return;
    setActiveStep("entities");
    setLocalError("");
    setActiveFeedbackTab("messages");
  }, [open, deployment]);

  useEffect(() => {
    if (!feedback?.active) {
      setActiveFeedbackTab("messages");
      return;
    }

    setActiveFeedbackTab(feedback?.complete ? "messages" : "streaming");
  }, [feedback?.active, feedback?.complete]);

  if (!open) return null;

  const canContinue = activeStep !== "entities" || entityIds.length > 0;
  const feedbackActive = feedback?.active === true;
  const feedbackComplete = feedback?.complete === true;
  const sanitizedFeedbackMessage = sanitizeHtmlFragment(feedback?.message || "Manual run completed.");
  const okText = feedbackActive ? (feedbackComplete ? "Close" : "Running...") : (activeStep === "options" ? "Run" : "Next");
  const currentError = activeStep === "entities" ? error : (localError || error);
  const feedbackStatusClass = feedback?.success === false
    ? "resource-workspace-access-note--error"
    : "resource-workspace-access-note--success";
  const handleDialogOk = feedbackActive ? onCancel : handleOk;
  const feedbackTabs = [
    { key: "messages", label: "Messages" },
    { key: "streaming", label: "Status Log" }
  ];

  return (
    <DialogControl
      controlOrigin="destination-deployment-manual-run"
      title="Manual Run"
      openDialog={open}
      okText={okText}
      loadingOk={!feedbackActive && activeStep === "options" && busy}
      cancelText="Cancel"
      onOk={handleDialogOk}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={feedbackActive ? !feedbackComplete : (busy || !deployment || !canContinue)}
      disableCancel={busy && !feedbackComplete}
      hideCancel={feedbackActive && feedbackComplete}
    >
      <div className="resource-workspace-linked-record-add-dialog resource-workspace-deployment-manual-run-dialog">
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <div>
            <dt>Deployment</dt>
            <dd>{deploymentName || "Deployment"}</dd>
          </div>
          <div>
            <dt>Destination</dt>
            <dd>{getDisplayName(destination, defaults?.label || "Destination")}</dd>
          </div>
        </dl>
        {feedbackActive ? (
          <div className="resource-workspace-manual-run-feedback form-wrapper form-wrapper-tabs-top">
            <div className="form-tabs-container form-tabs-container-top" role="tablist" aria-label="Manual Run feedback">
              {feedbackTabs.map((tab) => (
                <div
                  key={tab.key}
                  className={`form-tab-rows ${activeFeedbackTab === tab.key ? "form-tab-row-active" : ""}`}
                  role="tab"
                  aria-selected={activeFeedbackTab === tab.key}
                  tabIndex={activeFeedbackTab === tab.key ? 0 : -1}
                  onClick={() => setActiveFeedbackTab(tab.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActiveFeedbackTab(tab.key);
                    }
                  }}
                >
                  <div className="form-tab">{tab.label}</div>
                </div>
              ))}
            </div>
            <div className={`form-fields-tab${activeFeedbackTab !== "messages" ? " form-fields-tab-inactive" : ""}`} role="tabpanel">
              <div className="resource-workspace-manual-run-feedback-tab-content">
                {feedbackComplete ? (
                  <div
                    className={`resource-workspace-access-note ${feedbackStatusClass}`}
                    role={feedback?.success === false ? "alert" : "status"}
                    dangerouslySetInnerHTML={{ __html: sanitizedFeedbackMessage }}
                  />
                ) : (
                  <div className="resource-workspace-access-note" role="status">
                    Manual run is still running. Returned messages will appear here when the operation completes.
                  </div>
                )}
                {feedbackComplete && feedback?.details?.length ? (
                  <div className="resource-workspace-manual-run-feedback-details">
                    {feedback.details.map((detail, index) => (
                      <div key={`${detail}-${index}`}>{detail}</div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={`form-fields-tab${activeFeedbackTab !== "streaming" ? " form-fields-tab-inactive" : ""}`} role="tabpanel">
              <div className="resource-workspace-manual-run-feedback-tab-content">
                <SseFeedbackControl
                  ref={feedbackRef}
                  manager={feedbackManager}
                  operationName={feedbackOperationName}
                  refId={feedbackRefId}
                  showControls={false}
                  maxMessages={250}
                  emptyMessage="Waiting for live feedback..."
                />
                {feedback?.streamError ? (
                  <div className="resource-workspace-access-note" role="status">
                    {feedback.streamError}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className={`resource-workspace-manual-run-wizard${feedbackActive ? " resource-workspace-manual-run-wizard--hidden" : ""}`}>
        <div className="resource-workspace-manual-run-header">
          <div className="resource-workspace-manual-run-steps" aria-label="Manual Run steps">
            {[
              ["entities", "Entity Objects"],
              ["mode", "Run Mode"],
              ["options", "Options"]
            ].map(([stepKey, label], index) => (
              <span key={stepKey} className={`resource-workspace-manual-run-step${activeStep === stepKey ? " active" : ""}`}>
                {index + 1}. {label}
              </span>
            ))}
          </div>
          {activeStep !== "entities" ? (
            <ButtonControl
              type="standard-small"
              className="resource-workspace-manual-run-back"
              onClick={handleBack}
              disabled={busy}
            >
              {"<< Back"}
            </ButtonControl>
          ) : null}
        </div>
        {currentError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{currentError}</div> : null}
        {activeStep === "entities" ? (
          <section className="resource-workspace-manual-run-section" aria-label="Select Entity Objects">
            <div className="resource-wizard__entity-scope-selector">
              <CardGridControl
                rows={entityRows}
                metadata={MANUAL_RUN_ENTITY_SELECTOR_METADATA}
                rowKey={(row) => row.ManualRunEntityObjectId}
                selectedRowKeys={entityIds}
                onSelectedRowKeysChange={(nextKeys) => updateValues({ ...(values || {}), eoids: nextKeys })}
                selectable
                multiSelect
                headerStatusResolver={getManualRunEntitySelectorHeaderStatus}
                activeFilterDefaultMode="both"
                activeFilterOptions={[
                  { key: "available", label: "Available" },
                  { key: "selected", label: "Selected" },
                  { key: "both", label: "Both" }
                ]}
                activeFilterRowPredicate={(row, { mode, isSelected }) => {
                  if (mode === "selected") return isSelected;
                  return true;
                }}
                toolbarLeadingContent={entitySelectorToolbarLeadingContent}
                disabled={busy}
                emptyContent="No entity objects are available for this deployment."
                minCardWidth="15rem"
                maxColumns={2}
              />
            </div>
          </section>
        ) : null}
        {activeStep === "mode" ? (
          <section className="resource-workspace-manual-run-section" aria-label="Select Run Mode">
            <div className="resource-wizard__entity-scope-selector">
              {managerKey === "warehouse" ? (
              <WarehouseStepSelectorControl
                className="resource-workspace-manual-run-card-grid--modes"
                value={values?.step}
                onChange={(nextStep, nextMode) => updateValues({ ...(values || {}), runMode: nextMode?.key, step: nextStep }, nextMode)}
                disabled={busy}
                emptyContent="No run modes are available for this destination."
                minCardWidth="15rem"
                maxColumns={2}
              />
              ) : (
              <CardGridControl
                className="resource-workspace-manual-run-card-grid--modes"
                rows={modeRows}
                cardFields={[
                  { FieldName: "DisplayName", DisplayName: "Run Mode", ShowOnCard: true },
                  { FieldName: "Details", DisplayName: "Description", CardDisplayName: "", ShowOnCard: true },
                  { FieldName: "Impact", DisplayName: "Impact", ShowOnCard: true }
                ]}
                rowKey={(row) => row.ModeKey}
                selectedRowKeys={selectedMode?.key ? [selectedMode.key] : []}
                onSelectedRowKeysChange={selectModeByKeys}
                selectable
                searchable={false}
                activeFilterEnabled={false}
                disabled={busy}
                emptyContent="No run modes are available for this destination."
                minCardWidth="15rem"
                maxColumns={2}
              />
              )}
            </div>
          </section>
        ) : null}
        {activeStep === "options" ? (
          <section className="resource-workspace-manual-run-section" aria-label="Configure Run Options">
            <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
              <h3>Options</h3>
              <p>{selectedMode?.description || "Review the supported options for this run mode."}</p>
            </div>
            {managerKey === "filestorage" && canEditSingleThreadMode ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "debug",
                  displayName: "Single Thread Mode (Admin Only)",
                  description: "Run one entity object at a time for troubleshooting.",
                  fieldType: SystemTypes.UIFieldType.CheckBox
                })}
                value={values?.debug !== false}
                disabled={busy}
                onChange={(nextValue) => updateField("debug", nextValue === true)}
              />
            ) : null}
            {canEditAdminManualRunOptions ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "processorType",
                  displayName: "Processor Type (Admin Only)",
                  description: "Choose where this manual run executes. Azure Batch is the default.",
                  fieldType: SystemTypes.UIFieldType.SelectList,
                  options: DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS,
                  isRequired: true
                })}
                selectData={DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS}
                value={values?.processorType || DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE}
                disabled={busy}
                onChange={(nextValue) => updateField("processorType", nextValue || DESTINATION_MANUAL_RUN_DEFAULT_PROCESSOR_TYPE)}
              />
            ) : null}
            {managerKey === "warehouse" && selectedMode?.supportsResetCursor ? (
              <>
                <FieldControl
                  fieldMetadata={buildDestinationManualRunField({
                    fieldName: "resetfromdate",
                    displayName: "Reset After Date",
                    description: "Move selected entity cursors back to this updated-date value before running.",
                    fieldType: SystemTypes.UIFieldType.DateTime || "DateTime"
                  })}
                  value={values?.resetfromdate || ""}
                  disabled={busy}
                  onChange={(nextValue) => updateField("resetfromdate", nextValue || "")}
                />
                <FieldControl
                  fieldMetadata={buildDestinationManualRunField({
                    fieldName: "resetfromvalue",
                    displayName: "Reset After Identifier",
                    description: "Move selected entity cursors back to this source identifier. Date takes priority when both are set.",
                    fieldType: SystemTypes.UIFieldType.TextBox
                  })}
                  value={values?.resetfromvalue || ""}
                  disabled={busy}
                  onChange={(nextValue) => updateField("resetfromvalue", nextValue || "")}
                />
              </>
            ) : null}
            {managerKey === "warehouse" && selectedMode?.supportsForceReconcile ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "fr",
                  displayName: "Reconcile Every Record",
                  description: "Re-check existing destination records during the load. Slower, but useful after mapping or data drift fixes.",
                  fieldType: SystemTypes.UIFieldType.CheckBox
                })}
                value={values?.fr === true}
                disabled={busy}
                onChange={(nextValue) => updateField("fr", nextValue === true)}
              />
            ) : null}
            {managerKey === "warehouse" && selectedMode?.supportsParallelProcessing && canEditAdminManualRunOptions ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "multiThread",
                  displayName: "Run in Parallel (Admin Only)",
                  description: "Process multiple objects at once, limited by deployment max concurrency.",
                  fieldType: SystemTypes.UIFieldType.CheckBox
                })}
                value={values?.multiThread === true}
                disabled={busy}
                onChange={(nextValue) => updateField("multiThread", nextValue === true)}
              />
            ) : null}
            {managerKey === "warehouse" && selectedMode?.supportsFirstRunReset ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "force",
                  displayName: "Reset First-Run State",
                  description: "Clear destination processing state before starting. Only useful for first-run recovery.",
                  fieldType: SystemTypes.UIFieldType.CheckBox
                })}
                value={values?.force === true}
                disabled={busy}
                onChange={(nextValue) => updateField("force", nextValue === true)}
              />
            ) : null}
            {managerKey === "externalsystem" && selectedMode?.supportsRecordLimit ? (
              <FieldControl
                fieldMetadata={buildDestinationManualRunField({
                  fieldName: "take",
                  displayName: "Record Limit",
                  description: "Maximum number of records to process for this one-time run.",
                  fieldType: SystemTypes.UIFieldType.TextBox
                })}
                value={values?.take || selectedMode?.defaultTake || "250"}
                disabled={busy}
                onChange={(nextValue) => updateField("take", nextValue || "")}
              />
            ) : null}
            {!hasVisibleOptions ? (
              <div className="resource-workspace-access-note">No options available.</div>
            ) : null}
          </section>
        ) : null}
        </div>
      </div>
    </DialogControl>
  );
};

export const WarehouseDeploymentDeployDialog = ({
  open,
  destination,
  deployment,
  entityObjects,
  sourceObjects = [],
  canEditProcessorType = false,
  values,
  busy,
  error,
  onChange,
  onCancel,
  onConfirm
}) => {
  const deploymentName = getDeploymentDisplayName(deployment, destination);
  const entityOptions = useMemo(
    () => getDestinationManualRunEntityOptions(entityObjects, deployment, destination),
    [destination, deployment, entityObjects]
  );
  const sourceObjectsById = useMemo(() => {
    const lookup = new Map();
    getArray({ sourceObjects }, "sourceObjects").forEach((sourceObject) => {
      const displayName = getManualRunSourceObjectDisplayName(sourceObject);
      if (!displayName) return;
      getManualRunSourceObjectIds(sourceObject).forEach((id) => lookup.set(id.toLowerCase(), displayName));
    });
    return lookup;
  }, [sourceObjects]);
  const entityObjectsById = useMemo(() => {
    const lookup = new Map();
    getArray({ entityObjects }, "entityObjects").forEach((entityObject) => {
      const id = getManualRunEntityObjectId(entityObject);
      if (id) lookup.set(id.toLowerCase(), entityObject);
    });
    return lookup;
  }, [entityObjects]);
  const entityRows = useMemo(() => entityOptions.map((option) => {
    const entityObject = entityObjectsById.get(String(option.Value || "").toLowerCase()) || {};
    return {
      ...entityObject,
      ManualRunEntityObjectId: option.Value,
      EntitySelectorName: option.Text,
      EntitySelectorObject: getString(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || option.Value,
      EntitySelectorSourceObjectOfRecord: getManualRunEntitySourceObjectDisplayName(entityObject, sourceObjectsById)
    };
  }), [entityObjectsById, entityOptions, sourceObjectsById]);
  const entityIds = useMemo(() => normalizeDestinationManualRunEntityIds(values?.eoids), [values?.eoids]);
  const selectableEntityIds = useMemo(() => entityOptions.map((option) => option.Value).filter(Boolean), [entityOptions]);
  const allEntitiesSelected = selectableEntityIds.length > 0 && entityIds.length === selectableEntityIds.length;
  const updateValues = useCallback((nextValues) => onChange?.({ ...(values || {}), ...(nextValues || {}) }), [onChange, values]);
  const selectAllEntities = useCallback(() => {
    updateValues({ eoids: allEntitiesSelected ? [] : selectableEntityIds });
  }, [allEntitiesSelected, selectableEntityIds, updateValues]);
  const validationMessage = validateWarehouseDeployValues(values);
  const currentError = error;
  const entitySelectorToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar-shell">
      <div className="resource-wizard__entity-scope-selector-toolbar">
        <span>{entityIds.length} selected</span>
        <div className="resource-wizard__entity-scope-selector-actions">
          <ButtonControl
            type="cancel-small"
            onClick={selectAllEntities}
            disabled={busy || entityOptions.length === 0}
          >
            {allEntitiesSelected ? "Unselect All" : "Select All"}
          </ButtonControl>
        </div>
      </div>
      {validationMessage ? <span className="resource-wizard__entity-scope-selector-message">{validationMessage}</span> : null}
    </div>
  ), [allEntitiesSelected, busy, entityIds.length, entityOptions.length, selectAllEntities, validationMessage]);

  return (
    <DialogControl
      controlOrigin="warehouse-deployment-deploy"
      title="Deploy Warehouse"
      openDialog={open}
      okText="Deploy"
      loadingOk={busy}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy || !deployment || Boolean(validationMessage)}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog resource-workspace-deployment-manual-run-dialog">
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <div>
            <dt>Deployment</dt>
            <dd>{deploymentName || "Deployment"}</dd>
          </div>
          <div>
            <dt>Destination</dt>
            <dd>{getDisplayName(destination, "Warehouse Destination")}</dd>
          </div>
        </dl>
        {currentError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{currentError}</div> : null}
        <section className="resource-workspace-manual-run-section" aria-label="Select Entity Objects">
          <div className="resource-wizard__entity-scope-selector">
            <CardGridControl
              className="resource-workspace-deployment-entity-grid"
              rows={entityRows}
              metadata={MANUAL_RUN_ENTITY_SELECTOR_METADATA}
              rowKey={(row) => row.ManualRunEntityObjectId}
              selectedRowKeys={entityIds}
              onSelectedRowKeysChange={(nextKeys) => updateValues({ eoids: nextKeys })}
              selectable
              multiSelect
              headerStatusResolver={getManualRunEntitySelectorHeaderStatus}
              activeFilterDefaultMode="both"
              activeFilterOptions={[
                { key: "available", label: "Available" },
                { key: "selected", label: "Selected" },
                { key: "both", label: "Both" }
              ]}
              activeFilterRowPredicate={(row, { mode, isSelected }) => {
                if (mode === "selected") return isSelected;
                return true;
              }}
              toolbarLeadingContent={entitySelectorToolbarLeadingContent}
              disabled={busy}
              emptyContent="No entity objects are available for this deployment."
              minCardWidth="15rem"
              maxColumns={2}
            />
          </div>
        </section>
        <div className="resource-workspace-deployment-manual-run-options">
          <FieldControl
            fieldMetadata={buildDestinationManualRunField({
              fieldName: "includeViews",
              displayName: "Deploy Warehouse Views",
              description: "Validate and deploy active managed warehouse views after warehouse objects are deployed.",
              fieldType: SystemTypes.UIFieldType.CheckBox
            })}
            value={values?.includeViews === true}
            disabled={busy}
            onChange={(nextValue) => updateValues({ includeViews: nextValue === true })}
          />
          <FieldControl
            fieldMetadata={buildDestinationManualRunField({
              fieldName: "as",
              displayName: "Run addons & Post deployment scripts",
              description: "Run deployment addons and post-deployment scripts after warehouse objects are deployed. Managed warehouse views are controlled by Deploy Warehouse Views.",
              fieldType: SystemTypes.UIFieldType.CheckBox
            })}
            value={values?.as === true}
            disabled={busy}
            onChange={(nextValue) => updateValues({ as: nextValue === true })}
          />
        </div>
        {canEditProcessorType ? (
          <FieldControl
            fieldMetadata={buildDestinationManualRunField({
              fieldName: "processorType",
              displayName: "Processor Type (Admin Only)",
              description: "Choose where this deploy operation executes. Azure Batch is the default.",
              fieldType: SystemTypes.UIFieldType.SelectList,
              options: DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS,
              isRequired: true
            })}
            selectData={DESTINATION_MANUAL_RUN_PROCESSOR_TYPE_OPTIONS}
            value={values?.processorType || DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE}
            disabled={busy}
            onChange={(nextValue) => updateValues({ processorType: nextValue || DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE })}
          />
        ) : null}
        {currentError && !error ? (
          <div className="resource-workspace-access-note" role="status">{currentError}</div>
        ) : null}
      </div>
    </DialogControl>
  );
};

export const WarehouseDeploymentDropDialog = ({
  open,
  destination,
  deployment,
  busy,
  error,
  onCancel,
  onConfirm
}) => {
  const deploymentName = getDeploymentDisplayName(deployment, destination);
  return (
    <DialogControl
      controlOrigin="warehouse-deployment-drop"
      title="Drop Warehouse"
      openDialog={open}
      okText="Drop"
      loadingOk={busy}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy || !deployment}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <p>Drop "{deploymentName || "this deployment"}"? Physical warehouse objects for this deployment may be dropped.</p>
        {error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div> : null}
      </div>
    </DialogControl>
  );
};

export const WarehouseDeploymentCloneDialog = ({
  open,
  destination,
  deployment,
  schemaVersionOptions = [],
  values,
  busy,
  error,
  onChange,
  onCancel,
  onConfirm
}) => {
  const deploymentName = getDeploymentDisplayName(deployment, destination);
  const hasDatabaseName = Boolean(String(values?.dbn || "").trim());
  const updateField = useCallback((fieldName, nextValue) => {
    onChange?.({
      ...(values || {}),
      [fieldName]: nextValue || ""
    });
  }, [onChange, values]);
  const schemaVersionSelectData = useMemo(() => [
    { Text: "Use source deployment schema version", Value: "" },
    ...schemaVersionOptions
  ], [schemaVersionOptions]);

  return (
    <DialogControl
      controlOrigin="warehouse-deployment-clone"
      title="Clone Warehouse Deployment"
      openDialog={open}
      okText="Clone"
      loadingOk={busy}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy || !deployment || !hasDatabaseName}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <div>
            <dt>Source Deployment</dt>
            <dd>{deploymentName || "Deployment"}</dd>
          </div>
        </dl>
        {error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div> : null}
        <FieldControl
          fieldMetadata={buildDestinationManualRunField({
            fieldName: "dbn",
            displayName: "Database Name",
            description: "Database name for the cloned warehouse deployment.",
            fieldType: SystemTypes.UIFieldType.TextBox,
            isRequired: true
          })}
          value={values?.dbn || ""}
          disabled={busy}
          onChange={(nextValue) => updateField("dbn", nextValue)}
        />
        <FieldControl
          fieldMetadata={buildDestinationManualRunField({
            fieldName: "whn",
            displayName: "Warehouse Name",
            description: "Optional warehouse name for systems that use a separate warehouse target.",
            fieldType: SystemTypes.UIFieldType.TextBox
          })}
          value={values?.whn || ""}
          disabled={busy}
          onChange={(nextValue) => updateField("whn", nextValue)}
        />
        <FieldControl
          fieldMetadata={buildDestinationManualRunField({
            fieldName: "svid",
            displayName: "Schema Version",
            description: "Optional schema version for the cloned deployment.",
            fieldType: SystemTypes.UIFieldType.SelectList,
            options: schemaVersionSelectData
          })}
          selectData={schemaVersionSelectData}
          value={values?.svid || ""}
          disabled={busy}
          onChange={(nextValue) => updateField("svid", nextValue)}
        />
      </div>
    </DialogControl>
  );
};

export const DestinationEntityDeploymentPickerDialog = ({
  open,
  entityObjects,
  sourceObjects,
  deployments,
  existingEntityDeployments,
  selectedDeploymentId,
  selectedEntityObjectIds,
  busy,
  error,
  onChangeSelectedDeploymentId,
  onChangeSelectedEntityObjectIds,
  onCancel,
  onConfirm
}) => {
  const deploymentOptions = useMemo(
    () => getArray({ deployments }, "deployments")
      .filter((deployment) => !isRecordDeleted(deployment))
      .map((deployment, index) => ({
        Text: getDeploymentDisplayName(deployment),
        Value: getDeploymentId(deployment) || getDeploymentRenderKey(deployment, null, index)
      }))
      .filter((option) => option.Value),
    [deployments]
  );
  const existingEntityObjectIds = useMemo(
    () => new Set(getArray({ existingEntityDeployments }, "existingEntityDeployments")
      .filter((row) =>
        normalizeCompare(getString(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "DeploymentId", "deploymentId"))
          === normalizeCompare(selectedDeploymentId)
      )
      .map((row) => normalizeCompare(getString(row, "EntityObjectId", "entityObjectId")))
      .filter(Boolean)),
    [existingEntityDeployments, selectedDeploymentId]
  );
  const availableEntityObjects = useMemo(
    () => getArray({ entityObjects }, "entityObjects").filter((entityObject) => !isRecordDeleted(entityObject)),
    [entityObjects]
  );
  const pickerSourceObjectsById = useMemo(() => {
    const lookup = new Map();
    getArray({ sourceObjects }, "sourceObjects").forEach((sourceObject) => {
      const displayName = getManualRunSourceObjectDisplayName(sourceObject);
      if (!displayName) return;
      getManualRunSourceObjectIds(sourceObject).forEach((id) => lookup.set(String(id).toLowerCase(), displayName));
    });
    return lookup;
  }, [sourceObjects]);
  const entitySelectorRows = useMemo(() => availableEntityObjects.map((entityObject) => {
    const entityObjectId = getId(entityObject);
    return {
      ...entityObject,
      EntitySelectorKey: entityObjectId,
      EntitySelectorName: getDisplayName(entityObject, getString(entityObject, "ObjectName", "objectName") || entityObjectId || "Entity Object"),
      EntitySelectorObject: getString(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "InternalName", "internalName") || entityObjectId,
      EntitySelectorSourceObjectOfRecord: getManualRunEntitySourceObjectDisplayName(entityObject, pickerSourceObjectsById)
    };
  }), [availableEntityObjects, pickerSourceObjectsById]);
  const selectableSelectedEntityObjectIds = useMemo(
    () => selectedEntityObjectIds.filter((entityObjectId) => !existingEntityObjectIds.has(normalizeCompare(entityObjectId))),
    [existingEntityObjectIds, selectedEntityObjectIds]
  );
  const selectedAndExistingEntityObjectIds = useMemo(() => {
    const rowIds = entitySelectorRows.map((row) => getId(row) || row.EntitySelectorKey).filter(Boolean);
    const existingRowIds = rowIds.filter((entityObjectId) => existingEntityObjectIds.has(normalizeCompare(entityObjectId)));
    return [...new Set([...existingRowIds, ...selectableSelectedEntityObjectIds])];
  }, [entitySelectorRows, existingEntityObjectIds, selectableSelectedEntityObjectIds]);
  const selectedEntityObjectIdSet = useMemo(
    () => new Set(selectedAndExistingEntityObjectIds.map((entry) => normalizeCompare(entry)).filter(Boolean)),
    [selectedAndExistingEntityObjectIds]
  );
  const availableEntitySelectorIds = useMemo(() => entitySelectorRows
    .filter((row) => !existingEntityObjectIds.has(normalizeCompare(getId(row) || row.EntitySelectorKey)))
    .map((row) => getId(row) || row.EntitySelectorKey)
    .filter(Boolean),
  [entitySelectorRows, existingEntityObjectIds]);
  const allAvailableEntitySelectorsSelected = availableEntitySelectorIds.length > 0
    && availableEntitySelectorIds.every((id) => selectedEntityObjectIdSet.has(normalizeCompare(id)));
  const updateSelectedEntityObjectIds = useCallback((nextEntityObjectIds = []) => {
    const nextSelectableEntityObjectIds = getArray({ nextEntityObjectIds }, "nextEntityObjectIds")
      .filter((entityObjectId) => !existingEntityObjectIds.has(normalizeCompare(entityObjectId)));
    onChangeSelectedEntityObjectIds(nextSelectableEntityObjectIds);
  }, [existingEntityObjectIds, onChangeSelectedEntityObjectIds]);
  const selectAllEntitySelectors = useCallback(() => {
    updateSelectedEntityObjectIds(availableEntitySelectorIds);
  }, [availableEntitySelectorIds, updateSelectedEntityObjectIds]);
  const unselectAllEntitySelectors = useCallback(() => {
    updateSelectedEntityObjectIds([]);
  }, [updateSelectedEntityObjectIds]);
  const entitySelectorToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar">
      <span>{selectedEntityObjectIdSet.size.toLocaleString()} selected</span>
      <div className="resource-wizard__entity-scope-selector-actions">
        {allAvailableEntitySelectorsSelected ? (
          <ButtonControl
            type="cancel-small"
            onClick={unselectAllEntitySelectors}
            disabled={busy}
          >
            Unselect All
          </ButtonControl>
        ) : (
          <ButtonControl
            type="cancel-small"
            onClick={selectAllEntitySelectors}
            disabled={busy || !availableEntitySelectorIds.length}
          >
            Select All
          </ButtonControl>
        )}
      </div>
    </div>
  ), [
    allAvailableEntitySelectorsSelected,
    availableEntitySelectorIds.length,
    busy,
    selectAllEntitySelectors,
    selectedEntityObjectIdSet.size,
    unselectAllEntitySelectors
  ]);
  const selectedCount = selectableSelectedEntityObjectIds.length;

  return (
    <DialogControl
      title="Generate Entity Deployment Scope"
      openDialog={open}
      okText={busy ? "Generating..." : "Generate Selected"}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={busy || selectedCount === 0 || deploymentOptions.length === 0}
      disableCancel={busy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        {error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div> : null}
        {deploymentOptions.length > 1 ? (
          <FieldControl
            fieldMetadata={{
              ...buildSelectListField("EntityDeploymentTargetDeployment", "Deployment", deploymentOptions),
              PlaceHolder: "Select deployment"
            }}
            value={selectedDeploymentId}
            disabled={busy}
            onChange={(nextValue) => onChangeSelectedDeploymentId?.(nextValue || "")}
          />
        ) : null}
        {deploymentOptions.length === 0 ? (
          <ResourceStateControl compact tone="warning" message="Add a deployment before generating Entity Deployment Scope rows." />
        ) : null}
        {availableEntityObjects.length > 0 && deploymentOptions.length > 0 ? (
          <div className="resource-wizard__entity-scope-selector">
            <CardGridControl
              rows={entitySelectorRows}
              metadata={ENTITY_DEPLOYMENT_PICKER_METADATA}
              rowKey={(row) => getId(row) || row.EntitySelectorKey}
              selectedRowKeys={selectedAndExistingEntityObjectIds}
              onSelectedRowKeysChange={updateSelectedEntityObjectIds}
              selectableRowDisabledResolver={(row) => existingEntityObjectIds.has(normalizeCompare(getId(row) || row.EntitySelectorKey))}
              showSelectionLockIcon={false}
              selectable={true}
              multiSelect={true}
              headerStatusResolver={getEntityDeploymentPickerHeaderStatus}
              activeFilterDefaultMode="both"
              activeFilterOptions={[
                { key: "available", label: "Available" },
                { key: "selected", label: "Selected" },
                { key: "both", label: "Both" }
              ]}
              activeFilterRowPredicate={(row, { mode, isSelected }) => {
                if (mode === "selected") return isSelected;
                if (mode === "both") return true;
                return !existingEntityObjectIds.has(normalizeCompare(getId(row) || row.EntitySelectorKey));
              }}
              toolbarLeadingContent={entitySelectorToolbarLeadingContent}
              disabled={busy}
              emptyContent="No Entity Objects are available for the selected active schema version."
              minCardWidth="15rem"
              maxColumns={3}
            />
          </div>
        ) : (
          deploymentOptions.length > 0 ? (
            <ResourceStateControl compact message="No Entity Objects are available for the active schema version." />
          ) : null
        )}
      </div>
    </DialogControl>
  );
};
